<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import ModalDialog from '../UI/Modal/ModalDialog.vue'
import ButtonAll from '../UI/Button/ButtonAll.vue'
import useLoading from '../../../../Composables/useLoading.ts'
import useModal from '../../../../Composables/useModal.ts'
import FormCreator from '../UI/Form/FormCreator.vue'
import LazyContent from '../UI/Loading/LazyContent.vue'
import type { InvoiceItem } from '../../../../Types/invoice'
import { useInvoiceItem } from '../../../../Composables/Invoice/useInvoiceItem.ts'

type FormCreatorType = InstanceType<typeof FormCreator>
type FormDefParameters = {
  purchaseOrderItemId?: number
}

const props = defineProps<{
  invoiceId: number
  purchaseOrderItemId: number
}>()

const emit = defineEmits<{
  close: []
  save: [{ invoiceItem: InvoiceItem }]
}>()

const { t } = useI18n()
const { loading, setLoading } = useLoading(true)
const { loading: sending } = useLoading(false)
const { modal, modalId, open, close } = useModal({ prefix: 'invoice-item-' })
const form = ref<FormCreatorType | null>(null)
const modalTitle = computed(() => t('invoiceItem.action.addInvoiceItem'))

const endpoint = computed(() => `invoices/${props.invoiceId}/invoice-items`)
const defParams = computed<FormDefParameters>(() => ({ purchaseOrderItemId: props.purchaseOrderItemId }))

async function submit() {
  sending.value = true
  try {
    await form.value?.methods()
  } finally {
    sending.value = false
  }
}

function submitted(invoiceItem: InvoiceItem) {
  emit('save', { invoiceItem })
  close()
}

function onFormLoad(formCreator: FormCreatorType) {
  form.value = formCreator
}

onMounted(() => {
  open()
})
</script>

<template>
  <ModalDialog :id="modalId" ref="modal" :title="modalTitle" class-body="px-3" @icon-close="emit('close')">
    <template #body>
      <FormCreator
        :endpoint="endpoint"
        :params="defParams"
        send-params
        src-error="forminvoiceitem"
        class-to-add="d-none"
        @form-return="submitted"
        @update-data="onFormLoad"
        @is-loading="setLoading"
      />
      <LazyContent :loading="loading" :class="{ 'p-2': loading }">
        <div v-if="form" class="row">
          <div class="col-12">
            <FieldCreator name="itemType" form-ref="forminvoiceitem" :form-data="form" domain="invoiceItem" label-top />
          </div>
          <div class="col-12">
            <FieldCreator name="name" form-ref="forminvoiceitem" :form-data="form" domain="invoiceItem" label-top />
          </div>
          <div class="col-12">
            <FieldCreator
              name="description"
              form-ref="forminvoiceitem"
              :form-data="form"
              domain="invoiceItem"
              label-top
            />
          </div>
          <div class="col-12">
            <FieldCreator name="taxType" form-ref="forminvoiceitem" :form-data="form" domain="invoiceItem" label-top />
          </div>
          <div class="col-12">
            <FieldCreator
              name="unitAmount"
              form-ref="forminvoiceitem"
              :form-data="form"
              domain="invoiceItem"
              label-top
            />
          </div>
          <div class="col-12">
            <FieldCreator name="quantity" form-ref="forminvoiceitem" :form-data="form" domain="invoiceItem" label-top />
          </div>
          <div class="col-12">
            <FieldCreator name="amount" form-ref="forminvoiceitem" :form-data="form" domain="invoiceItem" label-top />
          </div>
          <div class="col-12">
            <FieldCreator
              name="vatAmount"
              form-ref="forminvoiceitem"
              :form-data="form"
              domain="invoiceItem"
              label-top
            />
          </div>
          <div class="col-12">
            <FieldCreator
              name="taxRoundingDifference"
              form-ref="forminvoiceitem"
              :form-data="form"
              domain="invoiceItem"
              label-top
            />
          </div>
          <div class="col-12">
            <FieldCreator
              name="equipment"
              form-ref="forminvoiceitem"
              :form-data="form"
              domain="invoiceItem"
              label-top
            />
          </div>
        </div>
      </LazyContent>
    </template>
    <template v-if="!loading" #footer>
      <div class="d-flex w-100 justify-content-end">
        <ButtonAll margin="no" type="default" label="button.cancel" @click="close" />
        <ButtonAll margin="no" type="action" label="button.confirm" class="ml-2" :loading="sending" @click="submit" />
      </div>
    </template>
  </ModalDialog>
</template>
